import { call, put, takeLatest } from "redux-saga/effects";
import { Endpoints } from "services/api";
import errorHandler from "common/errorHandler";
import * as actions from "./actions";

/**
 * Sagas for wizard-time schema profiling (PRE-2716).
 *
 * The connection flow is two hops chained into one request so the wizard has a
 * single loading state:
 *   1. system-connection-service returns a bounded sample (PRE-2717)
 *   2. data-parser-service profiles those rows and returns the proposal
 *
 * The file flow is a single hop: data-parser-service samples and profiles.
 */

function* postSchemaProfile(api, profileConfig) {
    const result = yield call([api, api.post], `${Endpoints.dataParser}/api/v1/schema/profile`, profileConfig);
    return result.data;
}

function* postConnectionSample(api, { connectionId, connectionType, objectName, fields, sampleSize }) {
    const result = yield call(
        [api, api.post],
        `${Endpoints.systemConnections}/api/v1/connections/item/${connectionId}/sample`,
        { connectionType, objectName, fields, sampleSize },
    );
    return result.data;
}

export function* getSchemaProfileRequest(api, { profileConfig }) {
    try {
        const schemaProfile = yield call(postSchemaProfile, api, profileConfig);
        yield put(actions.getSchemaProfileSuccess(schemaProfile));
    } catch (error) {
        // 422 means the source declares structure but has no rows to sample
        // (a DDL, or a schema-definition file). Not an error the user caused.
        const status = error?.response?.status;
        const data = error?.response?.data;
        if (status === 422) {
            yield put(actions.getSchemaProfileUnsupported({ reason: data?.error, message: data?.message }));
            return;
        }
        const errorObject = errorHandler(error, "Get", "schema profile");
        yield put(actions.getSchemaProfileFailure(errorObject));
    }
}

export function* sampleConnectionRequest(api, params) {
    try {
        const sampleResult = yield call(postConnectionSample, api, params);

        if (!sampleResult?.supported) {
            yield put(
                actions.getSchemaProfileUnsupported({
                    reason: sampleResult?.reason || "SAMPLING_UNAVAILABLE",
                    message: sampleResult?.message || "This connection cannot be sampled.",
                }),
            );
            return;
        }

        const schemaProfile = yield call(postSchemaProfile, api, {
            sample: sampleResult.sample,
            sampleSize: params.sampleSize,
            options: params.options,
        });
        yield put(actions.getSchemaProfileSuccess(schemaProfile));
    } catch (error) {
        const errorObject = errorHandler(error, "Get", "connection sample");
        yield put(actions.getSchemaProfileFailure(errorObject));
    }
}

export function* watchGetSchemaProfileRequest(api, { params }) {
    yield call(getSchemaProfileRequest, api, params);
}

export function* watchSampleConnectionRequest(api, { params }) {
    yield call(sampleConnectionRequest, api, params);
}

export default function* ({ api }) {
    yield takeLatest(actions.GET_SCHEMA_PROFILE_REQUEST, watchGetSchemaProfileRequest, api);
    yield takeLatest(actions.SAMPLE_CONNECTION_REQUEST, watchSampleConnectionRequest, api);
}
